import { useEffect, useState } from 'react';
import TileCard from '../TileCard';
import { getCachedResponse, getWorkflowRunsBreakdown } from '../../api/client';
import type { Env, WorkflowRunsBreakdown, WorkflowRow } from '../../types';

type SortKey = 'workflow' | 'total' | 'succeeded' | 'failed' | 'avgDurationMs' | 'lastRunTime';

const HELP =
  'Per-workflow run counts over the last 24 hours, sorted by volume. Click a column ' +
  'header to re-sort. If a workflow has failed runs, the last outcome column flags ' +
  'whether the most recent invocation was the failure (red) or a success (green).';

function compare(a: WorkflowRow, b: WorkflowRow, key: SortKey, dir: 1 | -1): number {
  const va = a[key];
  const vb = b[key];
  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
  return String(va).localeCompare(String(vb)) * dir;
}

function formatDuration(ms: number): string {
  if (!ms || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

function formatTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString();
}

function formatWindow(hours: number): string {
  if (hours < 24) return `${hours}h`;
  if (hours === 24) return '24h';
  if (hours % 24 === 0) return `${hours / 24}d`;
  return `${hours}h`;
}

interface Props {
  env: Env;
  windowHours?: number;
  refreshToken?: number;
}

export default function WorkflowRunsBreakdownTile({ env, windowHours = 24, refreshToken = 0 }: Props) {
  // Seed from localStorage cache if present so the page doesn't flash "Loading…"
  // when it opens from the overview's prefetch.
  const [data, setData] = useState<WorkflowRunsBreakdown | null>(() =>
    getCachedResponse<WorkflowRunsBreakdown>('workflow-runs-breakdown', { env, windowHours }),
  );
  const [loading, setLoading] = useState(data === null);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('total');
  const [sortDir, setSortDir] = useState<1 | -1>(-1);

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedResponse<WorkflowRunsBreakdown>('workflow-runs-breakdown', { env, windowHours });
    if (cached) setData(cached);
    setLoading(cached === null);
    setError(null);
    getWorkflowRunsBreakdown(env, windowHours)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, windowHours, refreshToken]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(key);
      setSortDir(key === 'workflow' ? 1 : -1);
    }
  }

  const rows = data?.rows ? [...data.rows].sort((a, b) => compare(a, b, sortKey, sortDir)) : [];
  const generatedFooter = data ? (
    <span>As of {new Date(data.generatedAt).toLocaleTimeString()} · {formatWindow(data.windowHours)} window · top 50</span>
  ) : undefined;

  const arrow = (k: SortKey) => (sortKey === k ? (sortDir === 1 ? ' ▲' : ' ▼') : '');

  return (
    <TileCard
      title={`Workflow breakdown — last ${formatWindow(windowHours)} (${env.toUpperCase()})`}
      help={HELP}
      loading={loading}
      error={error}
      footer={generatedFooter}
    >
      {data?.status === 'not-configured' && <p className="tile-empty">{data.message}</p>}
      {data?.status === 'error' && <div className="tile-error">Query failed: {data.message}</div>}
      {data?.status === 'ok' && rows.length === 0 && (
        <p className="tile-empty">No workflow runs found in the selected window.</p>
      )}
      {data?.status === 'ok' && rows.length > 0 && (
        <div className="workflow-table-wrap">
          <table className="workflow-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort('workflow')}>Workflow{arrow('workflow')}</th>
                <th onClick={() => toggleSort('total')} className="num">
                  Total{arrow('total')}
                </th>
                <th onClick={() => toggleSort('succeeded')} className="num">
                  OK{arrow('succeeded')}
                </th>
                <th onClick={() => toggleSort('failed')} className="num">
                  Fail{arrow('failed')}
                </th>
                <th onClick={() => toggleSort('avgDurationMs')} className="num">
                  Avg{arrow('avgDurationMs')}
                </th>
                <th onClick={() => toggleSort('lastRunTime')}>Last run{arrow('lastRunTime')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.workflow}>
                  <td>{r.workflow}</td>
                  <td className="num">{r.total}</td>
                  <td className="num ok">{r.succeeded}</td>
                  <td className={`num ${r.failed > 0 ? 'bad' : ''}`}>{r.failed}</td>
                  <td className="num">{formatDuration(r.avgDurationMs)}</td>
                  <td>
                    <span className={`pill pill-${r.lastRunStatus}`}>{r.lastRunStatus}</span>{' '}
                    <span className="muted">{formatTime(r.lastRunTime)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </TileCard>
  );
}
